import React, { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import OrderSummary from '@/components/checkout/OrderSummary';
import CheckoutForm from '@/components/checkout/CheckoutForm';
import PaymentStatus from '@/components/checkout/PaymentStatus';
import PaymentSuccess from '@/components/checkout/PaymentSuccess';
import PaymentFailed from '@/components/checkout/PaymentFailed';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

/**
 * Return a stable `client_session_id` scoped to (product, variant). Persisted in
 * sessionStorage so remounts within the same tab (React strict mode, route
 * transitions, browser back-forward) reuse the same Stripe PaymentIntent
 * instead of creating a fresh one on every mount.
 */
function useClientSessionId(productId, variantId) {
  return useMemo(() => {
    if (!productId) return '';
    const key = `pg_checkout_csid:${productId}:${variantId || 'base'}`;
    try {
      let v = sessionStorage.getItem(key);
      if (!v) {
        v = (crypto?.randomUUID?.() ||
             `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        sessionStorage.setItem(key, v);
      }
      return v;
    } catch {
      // Fallback if sessionStorage is blocked
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }, [productId, variantId]);
}

export default function CheckoutPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const returnedPi = params.get('payment_intent') || params.get('pi');
  const variantId = params.get('variant_id') || null;
  const clientSessionId = useClientSessionId(id, variantId);

  const [clientSecret, setClientSecret] = useState(null);
  const [piId, setPiId] = useState(returnedPi || null);
  const [summary, setSummary] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | processing | success | failed
  const [errorMsg, setErrorMsg] = useState(null);
  const [order, setOrder] = useState(null);

  // Bootstrap: create OR reuse a PaymentIntent for this product,
  // OR resolve a returning Stripe redirect.
  useEffect(() => {
    let alive = true;
    // The `alive` closure flag alone is sufficient to drop stale state updates
    // from a cancelled effect run. A previous `bootstrapRan` ref-guard caused
    // a race: when the effect re-ran (StrictMode / dep re-evaluation), the
    // cleanup from run #1 set alive=false, but run #2's early-return meant
    // NO bootstrap was ever active — the response from run #1's in-flight
    // POST was silently discarded, leaving phase permanently at 'loading'.

    // Cap the polling loop so a Stripe / webhook delay can't spin forever.
    // 12 attempts × 2500ms ≈ 30 seconds.
    const MAX_POLL_ATTEMPTS = 12;
    let pollAttempts = 0;

    async function bootstrap() {
      // Case 1: Stripe redirected back with a payment_intent → poll status
      if (returnedPi) {
        try {
          const { data } = await api.get(`/payment-intent/${returnedPi}`);
          if (!alive) return;
          if (data.status === 'succeeded') {
            setOrder(data.order || null);
            setPhase('success');
            return;
          }
          if (data.status === 'requires_payment_method' || data.status === 'canceled') {
            setErrorMsg('The last payment attempt did not complete.');
            setPhase('failed');
            return;
          }
          pollAttempts += 1;
          if (pollAttempts >= MAX_POLL_ATTEMPTS) {
            setErrorMsg(
              "We're still waiting on confirmation from Stripe. Your card may have been charged — " +
              "please check your email for a receipt, or click retry to check again."
            );
            setPhase('failed');
            return;
          }
          setPhase('processing');
          setTimeout(bootstrap, 2500);
          return;
        } catch (e) {
          if (!alive) return;
          setErrorMsg(e?.response?.data?.detail || 'Could not verify payment status.');
          setPhase('failed');
          return;
        }
      }

      // Case 2: Fresh checkout for a product id — server reuses an existing
      // PI when `client_session_id` matches so remounts don't create orphans.
      // For free products the server short-circuits Stripe entirely and returns
      // { is_free: true, order, order_summary } — we jump straight to success.
      try {
        const body = {
          product_id: id,
          quantity: 1,
          client_session_id: clientSessionId,
        };
        if (variantId) body.variant_id = variantId;
        const { data } = await api.post('/create-payment-intent', body);
        if (!alive) return;
        if (data.is_free) {
          setSummary(data.order_summary);
          setOrder(data.order || null);
          setPhase('success');
          return;
        }
        setClientSecret(data.client_secret);
        setPiId(data.payment_intent_id);
        setSummary(data.order_summary);
        setPhase('ready');
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.response?.data?.detail || 'Could not start checkout.');
        setPhase('failed');
      }
    }
    bootstrap();
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id, returnedPi]);   // clientSessionId is stable per (id, variantId); excluded to avoid re-fires

  const options = useMemo(() => clientSecret ? ({
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#c9a24a',
        colorBackground: '#0e1424',
        colorText: '#f2eee3',
        colorDanger: '#ff3ea5',
        colorTextPlaceholder: '#8a8577',
        fontFamily: 'Figtree, ui-sans-serif, system-ui, sans-serif',
        fontSizeBase: '14px',
        borderRadius: '10px',
      },
      rules: {
        '.Input': { border: '1px solid rgba(242,238,227,0.12)' },
        '.Tab': { border: '1px solid rgba(242,238,227,0.12)' },
        '.Label': { fontFamily: 'Source Code Pro, ui-monospace, monospace', textTransform: 'uppercase', letterSpacing: '0.16em', fontSize: '10px', color: '#8a8577' },
      },
    },
  }) : null, [clientSecret]);

  const handleSuccess = async (paymentIntent) => {
    setPhase('processing');
    // Poll a few times to allow the webhook to finalise the Order + digital download link
    for (let i = 0; i < 6; i++) {
      try {
        const { data } = await api.get(`/payment-intent/${paymentIntent.id}`);
        if (data.order && data.order.payment_status === 'paid') {
          setOrder(data.order);
          setPhase('success');
          return;
        }
      } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
    // Fallback — declare success even if order sync is briefly delayed
    setOrder(null);
    setPhase('success');
  };

  const retry = () => {
    setErrorMsg(null); setOrder(null);
    setPhase('loading');
    // If we're in the "returning from Stripe" flow, retry means re-poll the
    // existing PaymentIntent — do NOT create a new one (would double-charge).
    if (returnedPi) {
      let attempts = 0;
      const MAX = 12;
      const check = () => {
        api.get(`/payment-intent/${returnedPi}`)
          .then(({ data }) => {
            if (data.status === 'succeeded') { setOrder(data.order || null); setPhase('success'); return; }
            if (data.status === 'requires_payment_method' || data.status === 'canceled') {
              setErrorMsg('The last payment attempt did not complete.'); setPhase('failed'); return;
            }
            attempts += 1;
            if (attempts >= MAX) {
              setErrorMsg(
                "We're still waiting on confirmation from Stripe. Your card may have been charged — " +
                "please check your email for a receipt, or click retry to check again."
              );
              setPhase('failed');
              return;
            }
            setPhase('processing');
            setTimeout(check, 2500);
          })
          .catch((e) => {
            setErrorMsg(e?.response?.data?.detail || 'Could not verify payment status.');
            setPhase('failed');
          });
      };
      check();
      return;
    }
    // Fresh checkout: safe to create a new PaymentIntent (or resolve free order)
    setClientSecret(null); setPiId(null);
    const body = { product_id: id, quantity: 1, client_session_id: clientSessionId };
    if (variantId) body.variant_id = variantId;
    api.post('/create-payment-intent', body)
      .then(({ data }) => {
        if (data.is_free) { setSummary(data.order_summary); setOrder(data.order || null); setPhase('success'); return; }
        setClientSecret(data.client_secret); setPiId(data.payment_intent_id); setSummary(data.order_summary); setPhase('ready');
      })
      .catch((e) => { setErrorMsg(e?.response?.data?.detail || 'Could not start checkout.'); setPhase('failed'); });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14" data-testid="checkout-page">
      <Link to="/vault" className="text-[11px] font-mono tracking-widest text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={12} /> Back to Vault
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
        {/* LEFT / MAIN */}
        <div className="lg:col-span-3 order-2 lg:order-1 space-y-4">
          <div>
            <p className="pg-text-glow-gold font-mono text-[11px] tracking-[0.32em]">SECURE CHECKOUT</p>
            <h1 className="font-serif text-3xl lg:text-4xl text-[var(--pg-ink)] mt-2">Complete your purchase</h1>
          </div>

          {phase === 'loading' && <PaymentStatus label="Preparing your order…" />}
          {phase === 'processing' && <PaymentStatus />}
          {phase === 'failed' && <PaymentFailed message={errorMsg} onRetry={retry} />}
          {phase === 'success' && <PaymentSuccess order={order} summary={summary} />}

          {/* Keep <Elements> mounted through the 'processing' phase — Stripe.js
              requires the PaymentElement to remain mounted for the duration of
              stripe.confirmPayment(). Unmounting mid-submit throws a synchronous
              IntegrationError before any request is sent to api.stripe.com,
              which manifests as a permanent "Processing your payment…" hang. */}
          {(phase === 'ready' || phase === 'processing') && stripePromise && options && summary && (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                summary={summary}
                paymentIntentId={piId}
                onProcessing={() => setPhase('processing')}
                onSuccess={handleSuccess}
                onError={(m) => { setErrorMsg(m); setPhase('failed'); }}
              />
            </Elements>
          )}
          {!stripePromise && (
            <PaymentFailed message="Stripe is not configured. Please contact support." />
          )}
        </div>

        {/* RIGHT / SIDEBAR */}
        <div className="lg:col-span-2 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24">
            <OrderSummary summary={summary} loading={phase === 'loading'} />
          </div>
        </div>
      </div>
    </div>
  );
}
